import type { ReactNode } from 'react';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { CARD } from '@/components/ui';

/**
 * 加载骨架。
 *
 * 每页首屏结构不一样（配置页是复选行、数据页是指标九宫格、任务页是统计格），
 * 所以骨架按页面分型，尽量贴住真实布局，避免六页加载时长得一模一样、
 * 然后内容一到就整体跳一下。见 `qzapps/docs/conventions.md` §9。
 */

/** 一行「缩略图 + 两行文字」，对应账号行、作品行、任务行。 */
export function SkeletonRow({ avatar = true }: { avatar?: boolean }) {
  return (
    <div className="flex items-center gap-3">
      {avatar ? <Skeleton className="size-9 shrink-0 rounded" /> : null}
      <div className="min-w-0 flex-1 space-y-2">
        <Skeleton className="h-3.5 w-[58%]" />
        <Skeleton className="h-3 w-[34%]" />
      </div>
    </div>
  );
}

/** 一行「文字 + 右侧开关/计数」，对应配置页的复选行。 */
function SkeletonToggleRow() {
  return (
    <div className="flex items-center gap-3">
      <Skeleton className="h-3.5 w-[46%]" />
      <div className="ml-auto flex items-center gap-2">
        <Skeleton className="h-3 w-10" />
        <Skeleton className="h-5 w-9 rounded-full" />
      </div>
    </div>
  );
}

/** 编号步骤行（迁移页 StepCard）。 */
function SkeletonStep({ index }: { index: number }) {
  return (
    <div className="flex items-center gap-3">
      <Skeleton className="size-6 shrink-0 rounded-full" />
      <div className="min-w-0 flex-1 space-y-2">
        <Skeleton className="h-3.5 w-[52%]" />
        <Skeleton className="h-3 w-[30%]" />
      </div>
      <span className="sr-only">第 {index + 1} 步</span>
    </div>
  );
}

/** 底部通栏按钮占位。 */
function SkeletonButton() {
  return <Skeleton className="h-11 w-full rounded-control" />;
}

/** 两格统计卡（任务中心的「进行中 / 已结束」）。 */
function SkeletonStatPair() {
  return (
    <div className="grid grid-cols-2 gap-3">
      {[0, 1].map((cell) => (
        <div key={cell} className="bg-muted/60 rounded-icon px-3 py-2.5">
          <Skeleton className="h-3 w-10" />
          <Skeleton className="mt-2 h-5 w-8" />
        </div>
      ))}
    </div>
  );
}

/** 占位卡：可选标题条 + N 行。 */
export function SkeletonCard({
  rows = 3,
  title = true,
  header,
  children,
}: {
  rows?: number;
  title?: boolean;
  /** 卡片右上角的小控件（如「全选」按钮）占位 */
  header?: boolean;
  /** 自定义卡片主体（与 rows 互斥） */
  children?: ReactNode;
}) {
  return (
    <Card className={CARD}>
      {title || header ? (
        <div className="flex items-center justify-between gap-2">
          {title ? <Skeleton className="h-3.5 w-32" /> : <span />}
          {header ? <Skeleton className="h-3.5 w-10" /> : null}
        </div>
      ) : null}
      {children ?? (
        <div className="flex flex-col gap-3">
          {Array.from({ length: rows }, (_, index) => (
            <SkeletonRow key={index} />
          ))}
        </div>
      )}
    </Card>
  );
}

/** 指标九宫格的占位格（由调用方放进 `grid grid-cols-3` 容器里）。 */
export function MetricGridSkeleton({ count = 6 }: { count?: number }) {
  return (
    <>
      {Array.from({ length: count }, (_, index) => (
        <div key={index} className="rounded-control bg-muted/60 px-2 py-2.5">
          <Skeleton className="h-3 w-14" />
          <Skeleton className="mt-2 h-4 w-16" />
        </div>
      ))}
    </>
  );
}

export type PageSkeletonKind =
  | 'notes'
  | 'backup'
  | 'migration'
  | 'data'
  | 'posts'
  | 'tasks'
  | 'taskDetail'
  | 'note'
  | 'batch'
  | 'linkTool'
  | 'listTool'
  | 'actions'
  | 'commentCleaner'
  | 'commentBackup'
  | 'export';

/**
 * 页面级骨架：按各页真实结构拼。
 * `rows` 只对「列表型」（notes / backup 的复选行）有意义。
 */
export function PageSkeleton({
  kind,
  rows = 4,
}: {
  kind: PageSkeletonKind;
  rows?: number;
}) {
  switch (kind) {
    // 笔记详情：识别卡（封面 + 标题 + 元信息）→ 操作列表 → 破坏性操作
    case 'note':
      return (
        <>
          <Card className={CARD}>
            <div className="flex items-center gap-3">
              <Skeleton className="size-14 shrink-0 rounded-lg" />
              <div className="flex flex-1 flex-col gap-2">
                <Skeleton className="h-3.5 w-44" />
                <Skeleton className="h-2.5 w-28" />
              </div>
            </div>
          </Card>
          <SkeletonCard rows={2} header />
          <Card className={CARD}>
            <Skeleton className="mx-auto h-3.5 w-24" />
          </Card>
        </>
      );

    // 整账号批量：账号卡 → 说明卡 → 开始按钮
    case 'batch':
      return (
        <>
          <SkeletonCard rows={1} />
          <Card className={CARD}>
            <Skeleton className="h-3.5 w-28" />
            <Skeleton className="h-2.5 w-full" />
            <Skeleton className="h-2.5 w-3/4" />
          </Card>
          <Card className={CARD}>
            <SkeletonButton />
          </Card>
        </>
      );

    // 粘贴链接类工具（无水印下载 / 注册时间）：页首一个大号「粘贴」按钮 + 说明
    case 'linkTool':
      return (
        <>
          <Skeleton className="h-21 w-full rounded-card" />
          <Skeleton className="mx-4 h-3 w-3/5" />
        </>
      );

    // 列表导出类工具（导出收藏）：账号卡 → 说明卡 → 开始按钮
    case 'listTool':
      return (
        <>
          <SkeletonCard rows={1} />
          <Card className={CARD}>
            <Skeleton className="h-2.5 w-full" />
            <Skeleton className="h-2.5 w-3/4" />
            <Skeleton className="h-2 w-1/2" />
          </Card>
          <SkeletonButton />
        </>
      );

    // 互动清理：账号卡 → 一组操作行（图标 + 两行字 + 右侧计数）
    case 'actions':
      return (
        <>
          <SkeletonCard rows={1} />
          <div className="flex flex-col gap-2">
            {Array.from({ length: 3 }, (_, index) => (
              <Card key={index} className={CARD}>
                <SkeletonRow />
              </Card>
            ))}
          </div>
        </>
      );

    // 评论清理：注意卡 → 账号卡 → 笔记列表卡
    case 'commentCleaner':
      return (
        <>
          <Card className={CARD}>
            <Skeleton className="h-3.5 w-16" />
            <Skeleton className="h-2.5 w-4/5" />
          </Card>
          <SkeletonCard rows={1} />
          <SkeletonCard rows={rows} header />
        </>
      );

    // 评论备份 / 导出笔记：（评论备份多一张说明卡）账号卡 → 说明卡（标题 + 两行）→ 开始按钮
    case 'commentBackup':
    case 'export':
      return (
        <>
          {kind === 'commentBackup' ? (
            <Card className={CARD}>
              <Skeleton className="h-3.5 w-16" />
              <Skeleton className="h-2.5 w-4/5" />
            </Card>
          ) : null}
          <SkeletonCard rows={1} />
          <Card className={CARD}>
            <Skeleton className="h-3.5 w-28" />
            <Skeleton className="h-2.5 w-full" />
            <Skeleton className="h-2.5 w-3/4" />
          </Card>
          <SkeletonButton />
        </>
      );

    // 账号卡 → 笔记列表（标题 + 全选 + 复选行）→ 底部操作按钮
    case 'notes':
      return (
        <>
          <SkeletonCard rows={1} />
          <SkeletonCard rows={rows} header />
          <Card className={CARD}>
            <SkeletonButton />
          </Card>
        </>
      );

    // 账号卡 → 数据类型复选卡 → 底部开始按钮
    case 'backup':
      return (
        <>
          <SkeletonCard rows={1} />
          <Card className={CARD}>
            <Skeleton className="h-3 w-full" />
            <div className="flex flex-col gap-4 pt-1">
              {Array.from({ length: rows }, (_, index) => (
                <SkeletonToggleRow key={index} />
              ))}
            </div>
          </Card>
          <Card className={CARD}>
            <SkeletonButton />
          </Card>
        </>
      );

    // 账号卡 → 编号步骤卡 → 底部按钮
    case 'migration':
      return (
        <>
          <SkeletonCard rows={1} />
          <Card className={CARD}>
            <Skeleton className="h-3.5 w-28" />
            <div className="flex flex-col gap-4 pt-1">
              {[0, 1, 2].map((index) => (
                <SkeletonStep key={index} index={index} />
              ))}
            </div>
          </Card>
          <Card className={CARD}>
            <SkeletonButton />
          </Card>
        </>
      );

    // 账号卡 → 全账号合计 → 指标九宫格 → 趋势图 → 作品列表
    case 'data':
      return (
        <>
          <SkeletonCard rows={1} />
          <Card className={CARD}>
            <Skeleton className="h-3.5 w-40" />
            <div className="mt-3 grid grid-cols-3 gap-2">
              <MetricGridSkeleton count={3} />
            </div>
          </Card>
          <Card className={CARD}>
            <Skeleton className="h-3.5 w-32" />
            <div className="mt-3 grid grid-cols-3 gap-2">
              <MetricGridSkeleton count={6} />
            </div>
            <Skeleton className="mt-3 h-24 w-full rounded-control" />
          </Card>
          <SkeletonCard rows={3} title={false} />
        </>
      );

    // 分段切换（已发布 / 已隐藏）→ 内容列表卡（薯薯 /shushu/notes）
    case 'posts':
      return (
        <>
          <Skeleton className="h-9 w-full rounded-control" />
          <Card className={CARD}>
            <div className="flex flex-col gap-4">
              {Array.from({ length: rows }, (_, index) => (
                <SkeletonRow key={index} />
              ))}
            </div>
          </Card>
        </>
      );

    // 统计双格 → 进行中列表卡 → 已结束列表卡
    case 'tasks':
      return (
        <>
          <Card className={CARD}>
            <SkeletonStatPair />
          </Card>
          <Card className={CARD}>
            <Skeleton className="h-4 w-16" />
            <div className="flex flex-col gap-3 pt-1">
              {Array.from({ length: 2 }, (_, index) => (
                <SkeletonRow key={index} />
              ))}
            </div>
          </Card>
          <Card className={CARD}>
            <Skeleton className="h-4 w-16" />
            <div className="flex flex-col gap-3 pt-1">
              <SkeletonRow />
            </div>
          </Card>
        </>
      );

    // 进度卡 → 明细卡 → 操作按钮
    case 'taskDetail':
    default:
      return (
        <>
          <Card className={CARD}>
            <Skeleton className="h-4 w-40" />
            <Skeleton className="mt-3 h-2 w-full rounded-full" />
            <Skeleton className="mt-3 h-3 w-52" />
          </Card>
          <Card className={CARD}>
            <Skeleton className="h-3.5 w-24" />
            <div className="flex flex-col gap-3 pt-1">
              {Array.from({ length: 3 }, (_, index) => (
                <SkeletonRow key={index} avatar={false} />
              ))}
            </div>
          </Card>
          <Card className={CARD}>
            <SkeletonButton />
          </Card>
        </>
      );
  }
}
